import {
  AbstractApiResponse,
  ADSyncRequest,
  ADTestConnectionData,
  CreateADConfigTaskResponse,
  DeleteDirectoryRequest,
  DirectoryItemFromApi,
  DirectoryType,
  DryRunResult,
  GetADConfigExtendedRequest,
  GetSyncExecutionAlertResponse,
  GetSyncExecutionAlertsRequest,
  GetSyncLogsRequest,
  GroupSyncRequest,
  GroupSyncResponse,
  GroupSyncTaskReponse,
  SyncExecution,
  TriggerADSyncRequest,
  UpdateAttributeMappingTaskRequest,
  UpdateAttributeMappingTaskResponse,
  UpdateDomainSelectionRequest,
  UpdateGroupAndUserMappingRequest,
  UpdateUserAttributeDefinitionsRequest,
  UserAttributeDefinition,
  UserAttributeMapping,
  VidmConnector,
  VidmConnectorWithDomain,
  VidmConnectorWithIwaDir,
  VidmDirectoryCompleteData,
  VidmHostBaseData,
  VidmTaskResponse
} from 'vrlcm-gui-types';
import {
  extractVidmTaskErrors,
  extractVidmTaskSuccessData,
  isVidmTaskJobSuccessful,
  joinErrorMessages,
  sortUserAttributeDefinition,
  vidmDataError
} from 'vrlcm-gui-utils';
import { EngineApi } from '../engine';
import { ProductApi } from '../lcops';
import { AuthznApiUri } from './uri';

export class DirectoryApi {
  public static async getDirectoriesInVidm(
    tenantName?: string,
    postInitFunction?: (cancelFunction: () => void) => void
  ) {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    try {
      const vidmData = await ProductApi.getVidmData(tenantName);
      const response = await EngineApi.triggerJobAndReturnResponse<
        VidmHostBaseData,
        VidmTaskResponse<DirectoryItemFromApi[]>
      >(AuthznApiUri.GET_ALL_DIRECTORIES_URL, vidmData, cf =>
        cancelFunctions.push(cf)
      );
      return {
        success: isVidmTaskJobSuccessful(response.data),
        data:
          extractVidmTaskSuccessData<DirectoryItemFromApi[]>(response.data) ||
          [],
        errors: extractVidmTaskErrors(response.data),
        when: new Date()
      };
    } catch (e) {
      return {
        success: false,
        errors: [vidmDataError(e)],
        when: new Date()
      };
    }
  }

  public static getConnectorList(
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<VidmConnector[]>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          VidmHostBaseData,
          VidmTaskResponse<VidmConnector[]>
        >(AuthznApiUri.GET_CONNECTORS_URL, vidmData, cf =>
          cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: extractVidmTaskSuccessData<VidmConnector[]>(res.data) || [],
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static getConnectorListWithDomainDetails(
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<VidmConnectorWithDomain[]>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          VidmHostBaseData,
          VidmTaskResponse<VidmConnectorWithDomain[]>
        >(AuthznApiUri.GET_CONNECTORS_WITH_DOMAIN_DETAILS_URL, vidmData, cf =>
          cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data:
            extractVidmTaskSuccessData<VidmConnectorWithDomain[]>(res.data) ||
            [],
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static getConnectorListWithIwaDir(
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<VidmConnectorWithIwaDir[]>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          VidmHostBaseData,
          VidmTaskResponse<VidmConnectorWithIwaDir[]>
        >(AuthznApiUri.GET_CONNECTORS_WITH_IWA_DIR_URL, vidmData, cf =>
          cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data:
            extractVidmTaskSuccessData<VidmConnectorWithIwaDir[]>(res.data) ||
            [],
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static searchSubGroups(
    directoryConfigId: string,
    groupDN: string,
    searchString?: string
  ): Promise<AbstractApiResponse<GroupSyncTaskReponse>> {
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          GroupSyncRequest & VidmHostBaseData,
          VidmTaskResponse<GroupSyncResponse | string>
        >(AuthznApiUri.ADD_AD_SEARCH_GROUPS_URL, {
          searchDns: [groupDN],
          directoryConfigId,
          searchString,
          ...vidmData
        }).then(res => {
          const parsedResponseData = extractVidmTaskSuccessData<
            GroupSyncResponse
          >(res.data as VidmTaskResponse<GroupSyncResponse>);
          const moreThan1000Groups =
            (JSON.stringify(res.data) || '')
              .toLowerCase()
              .indexOf('mappedGroupsLimitReached'.toLowerCase()) !== -1;
          return {
            success: moreThan1000Groups
              ? true
              : isVidmTaskJobSuccessful(res.data),
            data: {
              groups:
                parsedResponseData &&
                parsedResponseData.identityGroups[groupDN] &&
                parsedResponseData.identityGroups[groupDN].mappedGroupData
                  ? parsedResponseData.identityGroups[groupDN].mappedGroupData
                  : [],
              moreThan1000Groups
            },
            when: new Date(),
            errors: extractVidmTaskErrors(res.data)
          };
        });
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static getUserAttributeDefinitions(
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<UserAttributeDefinition[]>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          VidmHostBaseData,
          VidmTaskResponse<UserAttributeDefinition[]>
        >(AuthznApiUri.GET_AD_USER_DEFINITIONS_URL, vidmData, cf =>
          cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: sortUserAttributeDefinition(
            extractVidmTaskSuccessData<UserAttributeDefinition[]>(res.data) ||
              []
          ),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static syncDryRun(
    data: ADSyncRequest,
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<DryRunResult>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          VidmHostBaseData & ADSyncRequest,
          VidmTaskResponse<DryRunResult>
        >(AuthznApiUri.AD_SYNC_DRY_RUN_URL, { ...data, ...vidmData }, cf =>
          cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: extractVidmTaskSuccessData<DryRunResult>(res.data),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static getCompleteDirectoryDetail(
    data: GetADConfigExtendedRequest,
    postInitFunction?: (cancelFunction: () => void) => void,
    tenantName?: string
  ): Promise<AbstractApiResponse<VidmDirectoryCompleteData>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData(tenantName).then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          GetADConfigExtendedRequest & VidmHostBaseData,
          VidmTaskResponse<VidmDirectoryCompleteData>
        >(
          AuthznApiUri.GET_EXTENDED_AD_DIRECTORY_URL,
          {
            ...data,
            ...vidmData
          },
          cf => cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: extractVidmTaskSuccessData<VidmDirectoryCompleteData>(res.data),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static getAllCompleteDirectoryDetail(
    skipPartialDirectories: boolean,
    postInitFunction?: (cancelFunction: () => void) => void,
    tenantName?: string
  ): Promise<AbstractApiResponse<VidmDirectoryCompleteData[]>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return DirectoryApi.getDirectoriesInVidm(tenantName, cf =>
      cancelFunctions.push(cf)
    ).then(
      res => {
        if (res.success && res.data) {
          const shallowDirectories = res.data.filter(
            d =>
              (d.directoryConfig.type === DirectoryType.ACTIVE_DIRECTORY_IWA ||
                d.directoryConfig.type ===
                  DirectoryType.ACTIVE_DIRECTORY_LDAP) &&
              (skipPartialDirectories
                ? d.syncExecutions.some(s => s.success)
                : true)
          );
          return Promise.all(
            shallowDirectories.map(d =>
              DirectoryApi.getCompleteDirectoryDetail(
                {
                  directoryConfigId: d.directoryConfig.directoryId,
                  directoryType: d.directoryConfig.type
                },
                cf => cancelFunctions.push(cf),
                tenantName
              ).then(fullDirectory => ({ fullDirectory, shallowDirectory: d }))
            )
          ).then(directoryResponses => ({
            success: true,
            data: directoryResponses
              .filter(
                ({ fullDirectory }) =>
                  fullDirectory.success && fullDirectory.data
              )
              .map(
                ({ fullDirectory }) =>
                  fullDirectory.data as VidmDirectoryCompleteData
              ),
            errors: directoryResponses
              .filter(({ fullDirectory }) => !fullDirectory.success)
              .map(({ fullDirectory, shallowDirectory }) =>
                joinErrorMessages(
                  fullDirectory.errors,
                  `Failed to fetch '${shallowDirectory.directoryConfig.name}' details`
                )
              ),
            when: new Date()
          }));
        } else {
          return {
            success: false,
            errors: res.errors,
            when: res.when
          };
        }
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static deleteDirectory(
    data: DeleteDirectoryRequest,
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<UserAttributeDefinition[]>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          VidmHostBaseData & DeleteDirectoryRequest,
          VidmTaskResponse<any>
        >(AuthznApiUri.DELETE_AD_DIRECTORY_URL, { ...data, ...vidmData }, cf =>
          cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static triggerADSync(
    data: TriggerADSyncRequest
  ): Promise<AbstractApiResponse> {
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          TriggerADSyncRequest & VidmHostBaseData,
          VidmTaskResponse<undefined>
        >(AuthznApiUri.TRIGGER_AD_SYNC_URL, {
          ...data,
          ...vidmData
        }).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static updateUserAttributeData(
    userAttributeDefinitions: UserAttributeDefinition[]
  ): Promise<AbstractApiResponse<void>> {
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          UpdateUserAttributeDefinitionsRequest & VidmHostBaseData,
          VidmTaskResponse<UserAttributeDefinition[]>
        >(AuthznApiUri.UPDATE_AD_USER_DEFINITIONS_URL, {
          vidmUserAttributeDefinitions: userAttributeDefinitions,
          ...vidmData
        }).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static getSyncExecutionAlerts(
    data: GetSyncExecutionAlertsRequest,
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<GetSyncExecutionAlertResponse>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          VidmHostBaseData & GetSyncExecutionAlertsRequest,
          VidmTaskResponse<GetSyncExecutionAlertResponse>
        >(AuthznApiUri.GET_AD_SYNC_ALERTS_URL, { ...vidmData, ...data }, cf =>
          cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: extractVidmTaskSuccessData<GetSyncExecutionAlertResponse>(
            res.data
          ),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static getSyncExecutions(
    data: GetSyncLogsRequest,
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<SyncExecution[]>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          VidmHostBaseData & GetSyncLogsRequest,
          VidmTaskResponse<SyncExecution[]>
        >(AuthznApiUri.GET_AD_SYNC_LOGS_URL, { ...vidmData, ...data }, cf =>
          cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: extractVidmTaskSuccessData<SyncExecution[]>(res.data) || [],
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static triggerTestConnection(
    data: ADTestConnectionData,
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          ADTestConnectionData & VidmHostBaseData,
          VidmTaskResponse<undefined>
        >(
          AuthznApiUri.ADD_AD_TEST_CONNECTION_URL,
          {
            ...data,
            ...vidmData
          },
          cf => cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static triggerTestConnectionForMultipleDirectories(
    data: ADTestConnectionData[],
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<
    AbstractApiResponse<{
      [directoryName: string]: {
        success: boolean;
        errors?: string[];
      };
    }>
  > {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return Promise.all(
      data.map(d =>
        DirectoryApi.triggerTestConnection(d, cf => cancelFunctions.push(cf))
      )
    ).then(testResponses => {
      return {
        success: true,
        data: testResponses.reduce<{
          [directoryName: string]: {
            success: boolean;
            errors?: string[];
          };
        }>((map, dres, i) => {
          const directoryName = data[i].directoryConfig.name as string;
          map[directoryName] = {
            success: dres.success,
            errors: dres.errors
          };
          return map;
        }, {}),
        when: new Date()
      };
    });
  }

  /**
   * directoryConfigId === undefined indicates update operation otherwise it indicates
   * create operation.
   */
  public static createOrUpdateDirectoryConfig(
    data: ADTestConnectionData,
    directoryConfigId?: string
  ): Promise<AbstractApiResponse<CreateADConfigTaskResponse>> {
    const updatedPayload = !directoryConfigId
      ? data
      : {
          ...data,
          directoryConfig: {
            ...data.directoryConfig,
            directoryConfigId
          }
        };
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          ADTestConnectionData & VidmHostBaseData,
          VidmTaskResponse<CreateADConfigTaskResponse>
        >(AuthznApiUri.ADD_AD_CREATE_DIRECTORY_CONFIG_URL, {
          ...updatedPayload,
          ...vidmData
        }).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: extractVidmTaskSuccessData<CreateADConfigTaskResponse>(
            res.data
          ),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static createDirectoryConfig(data: ADTestConnectionData) {
    return DirectoryApi.createOrUpdateDirectoryConfig(data);
  }

  // @TODO: API in the backend side not implemented
  public static updateDirectoryConfig(
    directoryConfigId: string,
    data: ADTestConnectionData
  ) {
    return DirectoryApi.createOrUpdateDirectoryConfig(data, directoryConfigId);
  }

  public static updateAttributeMapping(
    directoryConfigId: string,
    userAttributeMappings: UserAttributeMapping[]
  ): Promise<AbstractApiResponse<UpdateAttributeMappingTaskResponse>> {
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          UpdateAttributeMappingTaskRequest & VidmHostBaseData,
          VidmTaskResponse<UpdateAttributeMappingTaskResponse>
        >(AuthznApiUri.ADD_AD_UPDATE_ATTRIBUTE_MAPPING_URL, {
          directoryConfigId,
          userAttributeMappings,
          ...vidmData
        }).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: extractVidmTaskSuccessData<UpdateAttributeMappingTaskResponse>(
            res.data
          ),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static updateDomainSelection(
    data: UpdateDomainSelectionRequest
  ): Promise<AbstractApiResponse> {
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          UpdateDomainSelectionRequest & VidmHostBaseData,
          VidmTaskResponse<undefined>
        >(AuthznApiUri.ADD_AD_UPDATE_DOMAIN_SELECTION_URL, {
          ...data,
          ...vidmData
        }).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static updateUserAndGroupMapping(
    data: UpdateGroupAndUserMappingRequest
  ): Promise<AbstractApiResponse<DryRunResult>> {
    return ProductApi.getVidmData().then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          UpdateGroupAndUserMappingRequest,
          VidmTaskResponse<DryRunResult>
        >(AuthznApiUri.ADD_AD_UPDATE_USER_AND_GROUP_MAPPINGS_URL, {
          ...data,
          ...vidmData
        }).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: extractVidmTaskSuccessData<DryRunResult>(res.data),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }
}
